/**
 * Burst (Momentum Burst) particle effects — entry burst, ambient idle,
 * input-active streaks, and resolve launch choreography.
 *
 * Gold/kinetic palette (vs DI's cyan/frozen). All visuals follow Sakurai principles.
 * Matches DI's layer count and choreography depth.
 */

import { BURST_TRAIL, BURST, BRACE } from '../../constants';
import { configParticle, BURST_GEM_PALETTE, type ParticleCore } from './types';

// Gold palette constants (matching BURST in constants.ts)
const GOLD = 0xffbb00;
const GOLD_BRIGHT = 0xffcc00;
const GOLD_DARK = 0x443300;
const AMBER = 0xff9900;
const AMBER_DARK = 0x6b3a00;

/**
 * Burst Entry — window open burst.
 * 6-layer Sakurai choreography matching DI entry depth:
 * converging motes → radial speed lines → dark contrast cavity →
 * core flash → delayed orbit sparks → shockwave + second ring.
 */
export function spawnBurstEntry(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
    // === Layer 1: Converging gold motes — energy gathering inward ===
    const moteCount = 18;
    for (let i = 0; i < moteCount; i++) {
        const angle = (i / moteCount) * Math.PI * 2;
        const dist = 65 + Math.random() * 25;
        const spawnX = cx + Math.cos(angle) * dist;
        const spawnY = cy + Math.sin(angle) * dist;
        const speed = 3.5 + Math.random() * 2.5;
        const moteColor = Math.random() < 0.3 ? 0xffffff : GOLD;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: spawnX, y: spawnY,
            color: moteColor,
            life: 0.25 + Math.random() * 0.1,
            scale: 0.35 + Math.random() * 0.25,
            vx: -Math.cos(angle) * speed, // inward
            vy: -Math.sin(angle) * speed,
            alpha: 0.85,
            friction: 0.92,
            freezeImmune: true,
        });
    }

    // === Layer 2: Radial speed lines — Sakurai "straight lines vs curves" ===
    const lineCount = 12;
    for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const life = 0.12 + Math.random() * 0.06;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 15,
            outerRadius: 45 + Math.random() * 25,
            expandSpeed: 90 + Math.random() * 50,
            color: Math.random() < 0.3 ? 0xffffff : GOLD,
            lineWidth: 1.5 + Math.random() * 1.0,
            life,
            maxLife: life,
            freezeImmune: true,
        });
    }

    // === Layer 3: Dark contrast cavity — Sakurai "mix in darker elements" ===
    for (let i = 0; i < 6; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 12,
            y: cy + (Math.random() - 0.5) * 12,
            color: 0x1a0a00,
            life: 0.18 + Math.random() * 0.12,
            scale: 1.0 + Math.random() * 0.6,
            vx: Math.cos(angle) * (0.5 + Math.random()),
            vy: Math.sin(angle) * (0.5 + Math.random()),
            alpha: 0.55,
            friction: 0.9,
            rotationSpeed: 0,
            addBlend: false,
            hard: false,
            freezeImmune: true,
        });
    }

    // === Layer 4: Core flash — bright soft glow at center, ultra-brief ===
    const flash = core.getInactive();
    if (flash) {
        configParticle(flash, core, {
            x: cx, y: cy,
            color: GOLD,
            life: 0.12,
            scale: 4.0,
            alpha: 0.7,
            friction: 0,
            rotationSpeed: 0,
            rotation: 0,
            hard: false,
            freezeImmune: true,
        });
    }

    // Dark amber backing (Sakurai: mix dark into bright)
    const darkFlash = core.getInactive();
    if (darkFlash) {
        configParticle(darkFlash, core, {
            x: cx, y: cy,
            color: 0x1a0a00,
            life: 0.15,
            scale: 2.8,
            alpha: 0.55,
            friction: 0,
            rotationSpeed: 0,
            rotation: 0,
            addBlend: false,
            hard: false,
            freezeImmune: true,
        });
    }

    // === Layer 5: Delayed clockwise orbit sparks (+80ms) — Sakurai "stagger timing" ===
    core.pendingBlasts.push({
        timestamp: Date.now() + 80,
        callback: () => {
            const orbitCount = 10;
            for (let i = 0; i < orbitCount; i++) {
                const angle = (i / orbitCount) * Math.PI * 2;
                const speed = 2.5 + Math.random() * 2;
                const angVel = angle + Math.PI * 0.35; // clockwise offset
                const sparkColor = Math.random() < 0.5 ? GOLD : 0xffffff;
                core.spawnParticleAtAngle(
                    cx + Math.cos(angle) * 22,
                    cy + Math.sin(angle) * 22,
                    angVel, speed,
                    sparkColor,
                    0.3 + Math.random() * 0.15,
                    0.25 + Math.random() * 0.15,
                    0, 0.96,
                    true, true, 0.85, 1.3,
                );
            }
        },
    });

    // === Layer 6: Expanding shockwave ring — anime burst circle ===
    core.spawnShockwave(cx, cy, GOLD, 65, 110, 2.5, 0.2);

    // Second ring — delayed, thinner, white for contrast
    core.pendingBlasts.push({
        timestamp: Date.now() + 50,
        callback: () => {
            core.spawnShockwave(cx, cy, 0xffffff, 48, 140, 1.5, 0.15);
        },
    });
}

/**
 * Burst Ambient — continuous gold energy during burst window.
 * Kinetic and fast (vs DI's frozen/slow). ~40% spawn rate per frame.
 */
export function spawnBurstAmbient(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
    if (Math.random() > 0.4) return;

    const orbAngle = Math.random() * Math.PI * 2;
    const dist = 35 + Math.random() * 18;
    const tangent = orbAngle + Math.PI / 2; // clockwise drift

    const moteColor = Math.random() < 0.15 ? 0xffffff
        : Math.random() < 0.25 ? GOLD_DARK // dark motes for depth
            : GOLD;

    const p = core.getInactive();
    if (!p) return;
    configParticle(p, core, {
        x: cx + Math.cos(orbAngle) * dist,
        y: cy + Math.sin(orbAngle) * dist,
        color: moteColor,
        life: 0.35 + Math.random() * 0.25,
        scale: 0.15 + Math.random() * 0.15,
        // Kinetic — faster than DI ambient
        vx: Math.cos(tangent) * (1.5 + Math.random() * 1.0),
        vy: Math.sin(tangent) * (1.5 + Math.random() * 1.0),
        alpha: moteColor === GOLD_DARK ? 0.55 : 0.7,
        friction: 0.97,
        addBlend: moteColor !== GOLD_DARK,
    });

    // Occasional energy flicker — random direction, like pent-up kinetic energy
    if (Math.random() < 0.2) {
        const flickerAngle = Math.random() * Math.PI * 2;
        const flickerDist = 30 + Math.random() * 20;
        core.spawnParticleAtAngle(
            cx + Math.cos(flickerAngle) * flickerDist,
            cy + Math.sin(flickerAngle) * flickerDist,
            flickerAngle + Math.PI / 2, 0.8,
            GOLD_BRIGHT,
            0.15 + Math.random() * 0.1,
            0.1 + Math.random() * 0.08,
            0, 0.9,
            true, true, 0.5,
        );
    }
}

/**
 * Burst Input Active — directional energy when holding input.
 * Upward: rocketpack (orange/red flame + dark smoke).
 * Horizontal/Down: momentum surge (speed lines + dust kick).
 * ~50% spawn rate — higher density than DI input active for kinetic feel.
 */
export function spawnBurstInputActive(
    core: ParticleCore,
    cx: number, cy: number,
    inputX: number, inputY: number,
    playerColor: number,
): void {
    if (Math.random() > 0.5) return;

    const inputAngle = Math.atan2(inputY, inputX);
    const isUpward = inputY < -0.3;

    if (isUpward) {
        // Rocketpack — flame below tank + dark smoke trailing
        const flameAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.6; // downward
        const flameSpeed = 3 + Math.random() * 4;
        const flameColor = Math.random() < 0.3 ? 0xffdd44 : 0xff6600;
        core.spawnParticleAtAngle(
            cx + (Math.random() - 0.5) * 12,
            cy + 20 + Math.random() * 5, // below tank
            flameAngle, flameSpeed,
            flameColor,
            0.15 + Math.random() * 0.1,
            0.35 + Math.random() * 0.3,
            0.05, 0.92,
            true, true, 0.85,
            1.8 + Math.random() * 0.8, // stretch for flame look
        );

        // Dark smoke (Sakurai: mix dark into flame)
        if (Math.random() < 0.5) {
            core.spawnParticleAtAngle(
                cx + (Math.random() - 0.5) * 14,
                cy + 22 + Math.random() * 8,
                flameAngle + (Math.random() - 0.5) * 0.3,
                1.0 + Math.random() * 1.5,
                0x1a0a00,
                0.25 + Math.random() * 0.15,
                0.5 + Math.random() * 0.3,
                0.02, 0.95,
                false, false, 0.5,
            );
        }

        // Occasional bright spark ejection
        if (Math.random() < 0.3) {
            const sparkAngle = Math.PI / 2 + (Math.random() - 0.5) * 1.2;
            core.spawnParticleAtAngle(
                cx + (Math.random() - 0.5) * 8,
                cy + 18,
                sparkAngle, 4 + Math.random() * 3,
                0xffffff,
                0.08 + Math.random() * 0.06,
                0.12 + Math.random() * 0.08,
                0.1, 0.94,
                true, true, 0.9,
                1.5,
            );
        }
    } else {
        // Momentum surge — gold streaks in held direction + speed lines
        const ringDist = 45;
        const streakX = cx + Math.cos(inputAngle) * (ringDist + Math.random() * 15);
        const streakY = cy + Math.sin(inputAngle) * (ringDist + Math.random() * 15);
        const streakColor = Math.random() < 0.3 ? 0xffffff : GOLD;
        core.spawnParticleAtAngle(
            streakX, streakY,
            inputAngle + (Math.random() - 0.5) * 0.4,
            3 + Math.random() * 4,
            streakColor,
            0.12 + Math.random() * 0.08,
            0.25 + Math.random() * 0.2,
            0, 0.93,
            true, true, 0.85,
            2.5 + Math.random() * 1.5, // stretch 2.5-4x — anime speed lines
        );

        // Dark wake behind (opposing direction)
        if (Math.random() < 0.4) {
            const wakeAngle = inputAngle + Math.PI;
            const wakeDist = ringDist - 5 - Math.random() * 10;
            const p = core.getInactive();
            if (!p) return;
            configParticle(p, core, {
                x: cx + Math.cos(wakeAngle) * wakeDist,
                y: cy + Math.sin(wakeAngle) * wakeDist,
                color: 0x1a0a00,
                life: 0.12 + Math.random() * 0.08,
                scale: 0.3 + Math.random() * 0.2,
                vx: Math.cos(wakeAngle) * (0.5 + Math.random() * 0.5),
                vy: Math.sin(wakeAngle) * (0.5 + Math.random() * 0.5),
                alpha: 0.45,
                friction: 0.9,
                rotationSpeed: 0,
                addBlend: false,
            });
        }

        // Perpendicular accent sparks — like DI's directional accents
        if (Math.random() < 0.25) {
            const side = Math.random() < 0.5 ? 1 : -1;
            const accentAngle = inputAngle + side * Math.PI / 2;
            core.spawnParticleAtAngle(
                cx + Math.cos(inputAngle) * ringDist,
                cy + Math.sin(inputAngle) * ringDist,
                accentAngle, 2 + Math.random() * 1.5,
                GOLD_BRIGHT,
                0.1 + Math.random() * 0.08,
                0.12 + Math.random() * 0.1,
                0, 0.9,
                true, true, 0.7,
                1.5,
            );
        }
    }
}

/**
 * Burst Resolve — window expire launch burst.
 * 8-layer Sakurai choreography matching DI resolve depth:
 * fanfare speed lines → bright launch flash → dark contrast plume →
 * velocity-aligned streaks → shockwave → smoke baton pass → gem confetti → end flash.
 */
export function spawnBurstResolve(
    core: ParticleCore,
    cx: number, cy: number,
    launchVx: number, launchVy: number,
    playerColor: number,
): void {
    const launchAngle = Math.atan2(launchVy, launchVx);
    const launchSpeed = Math.sqrt(launchVx * launchVx + launchVy * launchVy);
    const intensity = Math.min(1.0, launchSpeed / 10);

    // No impulse applied = subtle effect only
    if (intensity < 0.05) {
        // Minimal end pop
        const pop = core.getInactive();
        if (pop) {
            configParticle(pop, core, {
                x: cx, y: cy,
                color: GOLD,
                life: 0.08,
                scale: 1.5,
                alpha: 0.3,
                rotationSpeed: 0,
                rotation: 0,
                hard: false,
            });
        }
        return;
    }

    // === Layer 1: Fanfare speed lines — crisp radial lines in launch direction ===
    const lineCount = 8 + Math.floor(5 * intensity);
    for (let i = 0; i < lineCount; i++) {
        const spread = (Math.random() - 0.5) * Math.PI * 0.5;
        const angle = launchAngle + spread;
        const life = 0.1 + Math.random() * 0.08;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 10 + Math.random() * 5,
            outerRadius: (35 + Math.random() * 30) * intensity,
            expandSpeed: (120 + Math.random() * 70) * intensity,
            color: Math.random() < 0.3 ? 0xffffff : GOLD,
            lineWidth: 2.5 + intensity,
            life,
            maxLife: life,
            freezeImmune: false,
        });
    }

    // Opposing dark lines (Sakurai: dark behind bright for contrast)
    const darkLineCount = 4;
    for (let i = 0; i < darkLineCount; i++) {
        const spread = (Math.random() - 0.5) * Math.PI * 0.3;
        const angle = launchAngle + Math.PI + spread;
        const life = 0.08 + Math.random() * 0.04;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 8,
            outerRadius: 22 + Math.random() * 12,
            expandSpeed: 65 + Math.random() * 35,
            color: 0x1a0a00,
            lineWidth: 2.5,
            life,
            maxLife: life,
            freezeImmune: false,
        });
    }

    // === Layer 2: Bright launch flash — large soft glow at center ===
    const bigFlash = core.getInactive();
    if (bigFlash) {
        configParticle(bigFlash, core, {
            x: cx, y: cy,
            color: GOLD,
            life: 0.18,
            scale: 5.0 * intensity,
            vx: Math.cos(launchAngle) * 1.5,
            vy: Math.sin(launchAngle) * 1.5,
            alpha: 0.75,
            friction: 0.5,
            rotationSpeed: 0,
            rotation: 0,
            hard: false,
        });
    }

    // === Layer 3: Dark contrast plume — near-black particles behind bright launch ===
    const darkCount = 5 + Math.floor(4 * intensity);
    for (let i = 0; i < darkCount; i++) {
        const backAngle = launchAngle + Math.PI + (Math.random() - 0.5) * Math.PI * 0.6;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 10,
            y: cy + (Math.random() - 0.5) * 10,
            color: 0x1a0a00,
            life: 0.22 + Math.random() * 0.15,
            scale: 0.5 + Math.random() * 0.5,
            vx: Math.cos(backAngle) * (1.5 + Math.random() * 2.5),
            vy: Math.sin(backAngle) * (1.5 + Math.random() * 2.5),
            alpha: 0.55,
            gravity: 0.04,
            friction: 0.94,
            addBlend: false,
            hard: false,
        });
    }

    // === Layer 4: Velocity-aligned gold streaks — anime speed lines ===
    const streakCount = 10 + Math.floor(8 * intensity);
    for (let i = 0; i < streakCount; i++) {
        const spread = (Math.random() - 0.5) * Math.PI * 0.4;
        const angle = launchAngle + spread;
        const speed = (5 + Math.random() * 7) * intensity;
        const streakColor = Math.random() < 0.25 ? 0xffffff
            : Math.random() < 0.4 ? AMBER
                : GOLD;
        const dist = 15 + Math.random() * 10;
        const offX = cx + Math.cos(launchAngle) * dist + Math.cos(angle) * (Math.random() * 5);
        const offY = cy + Math.sin(launchAngle) * dist + Math.sin(angle) * (Math.random() * 5);
        core.spawnParticleAtAngle(
            offX, offY,
            angle, speed,
            streakColor,
            0.12 + Math.random() * 0.12,
            (0.35 + Math.random() * 0.35) * intensity,
            0.03, 0.95,
            true, true, 0.9,
            2.5 + Math.random() * 1.5,
        );
    }

    // === Layer 5: Directional shockwave — biased in launch direction ===
    core.spawnShockwave(
        cx + Math.cos(launchAngle) * 12,
        cy + Math.sin(launchAngle) * 12,
        GOLD,
        50 * intensity,
        150,
        2.5,
        0.18,
    );

    // === Layer 6: Smoke baton pass (+100ms) — Sakurai phase sequencing ===
    // Dark, slow, expanding — takes over as bright elements fade
    core.pendingBlasts.push({
        timestamp: Date.now() + 100,
        callback: () => {
            const smokeCount = 4 + Math.floor(3 * intensity);
            for (let i = 0; i < smokeCount; i++) {
                const angle = launchAngle + Math.PI + (Math.random() - 0.5) * Math.PI * 0.8;
                const dist = 8 + Math.random() * 12;
                core.spawnParticle(
                    cx + Math.cos(angle) * dist,
                    cy + Math.sin(angle) * dist,
                    Math.random() < 0.5 ? 0x1a0a00 : 0x2a1500,
                    0.3, 1.5,
                    0.4, 0.8,          // longer life — smoke lingers
                    0.8, 1.5,          // large, soft
                    -0.01,             // slight upward float
                    0.985,
                    false, false,      // NORMAL + soft — dark smoke
                    0.35,
                );
            }
        },
    });

    // === Layer 7: Gem confetti (+60ms) — gold palette ===
    core.pendingBlasts.push({
        timestamp: Date.now() + 60,
        callback: () => {
            const gemCount = 8 + Math.floor(5 * intensity);
            for (let i = 0; i < gemCount; i++) {
                const angle = launchAngle + (Math.random() - 0.5) * Math.PI * 0.7;
                const speed = 3.5 + Math.random() * 6;
                const gemColor = BURST_GEM_PALETTE[Math.floor(Math.random() * BURST_GEM_PALETTE.length)];
                const p = core.getInactive();
                if (!p) continue;
                configParticle(p, core, {
                    x: cx + Math.cos(launchAngle) * (10 + Math.random() * 15),
                    y: cy + Math.sin(launchAngle) * (10 + Math.random() * 15),
                    color: gemColor,
                    life: 0.35 + Math.random() * 0.25,
                    scale: 0.18 + Math.random() * 0.18,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    alpha: 0.9,
                    gravity: 0.12,
                    friction: 0.97,
                });
                p.sprite.texture = core.clusterTexture;
            }
        },
    });

    // === Layer 8: End flash (+160ms) — Sakurai Phase 4 punctuation ===
    core.pendingBlasts.push({
        timestamp: Date.now() + 160,
        callback: () => {
            const endFlash = core.getInactive();
            if (endFlash) {
                configParticle(endFlash, core, {
                    x: cx + Math.cos(launchAngle) * 15,
                    y: cy + Math.sin(launchAngle) * 15,
                    color: 0xffffff,
                    life: 0.08,
                    scale: 3.5 * intensity,
                    alpha: 0.65,
                    rotationSpeed: 0,
                    rotation: 0,
                    hard: false,
                });
            }
            const darkPop = core.getInactive();
            if (darkPop) {
                configParticle(darkPop, core, {
                    x: cx, y: cy,
                    color: 0x1a0a00,
                    life: 0.1,
                    scale: 2.5 * intensity,
                    alpha: 0.45,
                    rotationSpeed: 0,
                    rotation: 0,
                    addBlend: false,
                    hard: false,
                });
            }
        },
    });
}

/**
 * Burst Flight Trail — gold-palette speed-tiered trail during post-burst airborne flight.
 * 3 tiers, generous density. Gold palette differentiates from ragdoll trail.
 * Rocketpack exhaust for upward bursts. Afterimage + gem confetti at high speed.
 * Sakurai: short lifespans, dark+bright contrast, scale to event.
 */
export function spawnBurstTrail(core: ParticleCore, x: number, y: number, vx: number, vy: number, speed: number): void {
    const T = BURST_TRAIL;
    const angle = Math.atan2(vy, vx);
    const backAngle = angle + Math.PI;

    // Determine tier
    const tier = speed >= T.TIER_HIGH ? 3 : speed >= T.TIER_MED ? 2 : 1;

    // --- Gold wisps (all tiers) — soft glow, ADD blend ---
    // Sakurai: offset along travel direction to avoid center overlap waste.
    // Spread wisps along backward angle so they're already fanned out at spawn.
    const wispCount = tier === 1 ? 2 + Math.floor(Math.random() * 2) :
                      tier === 2 ? 3 + Math.floor(Math.random() * 2) :
                                   4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < wispCount; i++) {
        const trailOffset = Math.random() * 16; // spread along backward trail
        const perpOffset = (Math.random() - 0.5) * 14; // lateral scatter
        const ox = Math.cos(backAngle) * trailOffset + Math.cos(backAngle + Math.PI / 2) * perpOffset;
        const oy = Math.sin(backAngle) * trailOffset + Math.sin(backAngle + Math.PI / 2) * perpOffset;
        const life = T.WISP_LIFE_MIN + Math.random() * (T.WISP_LIFE_MAX - T.WISP_LIFE_MIN);
        const scale = T.WISP_SCALE_MIN + Math.random() * (T.WISP_SCALE_MAX - T.WISP_SCALE_MIN);
        const wispColor = Math.random() < 0.2 ? 0xffffff
            : Math.random() < 0.15 ? GOLD_BRIGHT : GOLD;
        core.spawnParticleAtAngle(
            x + ox, y + oy,
            Math.random() * Math.PI * 2, 0.3 + Math.random() * 0.6,
            wispColor,
            life, scale, 0, 0.93,
            true, false, T.WISP_ALPHA,
        );
    }

    // --- Gold aura glow (all tiers) — big soft halo around tank, speed-scaled ---
    const auraAlpha = T.AURA_ALPHA_MIN + (T.AURA_ALPHA_MAX - T.AURA_ALPHA_MIN) * Math.min(1.0, speed / T.TIER_HIGH);
    core.spawnParticleAtAngle(
        x, y, 0, 0, GOLD,
        T.AURA_LIFE, T.AURA_SCALE,
        0, 1.0, true, false, auraAlpha,
    );
    // Secondary dark-amber aura backing (Sakurai: dark behind bright for depth)
    if (tier >= 2) {
        core.spawnParticleAtAngle(
            x, y, 0, 0, GOLD_DARK,
            T.AURA_LIFE * 1.2, T.AURA_SCALE * 0.8,
            0, 1.0, false, false, auraAlpha * 0.6,
        );
    }

    // --- Dark smoke wake (all tiers) — NORMAL blend, Sakurai contrast ---
    // Offset along backward trail (same overlap-avoidance principle)
    const smokeCount = tier === 1 ? 1 : tier === 2 ? 2 : 3;
    for (let i = 0; i < smokeCount; i++) {
        const smokeTrailOff = Math.random() * 12;
        const smokePerpOff = (Math.random() - 0.5) * 10;
        const ox = Math.cos(backAngle) * smokeTrailOff + Math.cos(backAngle + Math.PI / 2) * smokePerpOff;
        const oy = Math.sin(backAngle) * smokeTrailOff + Math.sin(backAngle + Math.PI / 2) * smokePerpOff;
        const life = T.SMOKE_LIFE_MIN + Math.random() * (T.SMOKE_LIFE_MAX - T.SMOKE_LIFE_MIN);
        const scale = T.SMOKE_SCALE_MIN + Math.random() * (T.SMOKE_SCALE_MAX - T.SMOKE_SCALE_MIN);
        core.spawnParticleAtAngle(
            x + ox, y + oy,
            backAngle + (Math.random() - 0.5) * 1.2,
            0.3 + Math.random() * 0.6,
            T.SMOKE_COLOR, life, scale,
            -0.01, 0.95,
            false, false, T.SMOKE_ALPHA,
        );
    }

    // --- Gold stretched streaks (all tiers, count scales) — anime speed lines ---
    const streakCount = tier === 1 ? 1 + Math.floor(Math.random() * 2) :
                        tier === 2 ? 3 + Math.floor(Math.random() * 2) :
                                     5 + Math.floor(Math.random() * 3);
    for (let i = 0; i < streakCount; i++) {
        const spread = (Math.random() - 0.5) * 0.6;
        const ox = (Math.random() - 0.5) * 12;
        const oy = (Math.random() - 0.5) * 12;
        const streakColor = Math.random() < 0.25 ? 0xffffff
            : Math.random() < 0.35 ? AMBER : GOLD;
        core.spawnParticleAtAngle(
            x + ox, y + oy,
            angle + spread, 0.2 + Math.random() * 0.5,
            streakColor,
            T.STREAK_LIFE, T.STREAK_SCALE,
            0, 0.92,
            true, true, T.STREAK_ALPHA, T.STREAK_STRETCH,
        );
    }

    // --- Impact lines (tier 2+) — radiate backward, crisp geometric ---
    if (tier >= 2) {
        const lineCount = tier === 2 ? 1 + Math.floor(Math.random() * 2) : 2 + Math.floor(Math.random() * 2);
        for (let i = 0; i < lineCount; i++) {
            const spread = (Math.random() - 0.5) * Math.PI * 0.5;
            const life = T.LINE_LIFE;
            core.impactLines.push({
                x, y,
                angle: backAngle + spread,
                innerRadius: 5,
                outerRadius: 16 + Math.random() * 12,
                expandSpeed: T.LINE_EXPAND + Math.random() * 20,
                color: Math.random() < 0.3 ? 0xffffff : GOLD,
                lineWidth: T.LINE_WIDTH,
                life, maxLife: life, freezeImmune: false,
            });
        }
    }

    // --- Afterimage glow (tier 2+) — soft ghost at previous position ---
    if (tier >= 2) {
        core.spawnParticleAtAngle(
            x, y, 0, 0, GOLD,
            T.AFTERIMAGE_LIFE, T.AFTERIMAGE_SCALE,
            0, 1.0, true, false, T.AFTERIMAGE_ALPHA,
        );
    }

    // --- Gem confetti (tier 3) — gold palette chiclet sparkle ---
    if (tier >= 3 && Math.random() < 0.6) {
        const gemCount = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < gemCount; i++) {
            const gemAngle = angle + (Math.random() - 0.5) * Math.PI * 0.6;
            const gemSpeed = 1.5 + Math.random() * 3;
            const gemColor = BURST_GEM_PALETTE[Math.floor(Math.random() * BURST_GEM_PALETTE.length)];
            const p = core.getInactive();
            if (!p) continue;
            configParticle(p, core, {
                x: x + (Math.random() - 0.5) * 12,
                y: y + (Math.random() - 0.5) * 12,
                color: gemColor,
                life: 0.2 + Math.random() * 0.15,
                scale: 0.12 + Math.random() * 0.12,
                vx: Math.cos(gemAngle) * gemSpeed,
                vy: Math.sin(gemAngle) * gemSpeed,
                alpha: 0.85,
                gravity: 0.08,
                friction: 0.97,
            });
            p.sprite.texture = core.clusterTexture;
        }
    }

    // --- Rocketpack exhaust (all tiers when going upward, intensity scales with tier) ---
    if (vy < T.ROCKET_VY_THRESHOLD) {
        // Main flame — multiple particles for thick exhaust
        const flameCount = tier === 1 ? 1 : tier === 2 ? 2 : 3;
        for (let i = 0; i < flameCount; i++) {
            const flameAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.6; // downward with spread
            const flameSpeed = 3 + Math.random() * 4;
            const flameColor = Math.random() < 0.2 ? 0xffdd44
                : Math.random() < 0.4 ? 0xff4400 : T.ROCKET_FLAME_COLOR;
            core.spawnParticleAtAngle(
                x + (Math.random() - 0.5) * 14,
                y + 18 + Math.random() * 6,
                flameAngle, flameSpeed,
                flameColor,
                0.1 + Math.random() * 0.08,
                0.25 + Math.random() * 0.2,
                0.05, 0.91,
                true, true, 0.85,
                1.8 + Math.random() * 0.8,
            );
        }

        // Bright spark ejection (Sakurai: occasional crisp highlight in flame)
        if (Math.random() < 0.4) {
            const sparkAngle = Math.PI / 2 + (Math.random() - 0.5) * 1.0;
            core.spawnParticleAtAngle(
                x + (Math.random() - 0.5) * 8,
                y + 16,
                sparkAngle, 4 + Math.random() * 4,
                0xffffff,
                0.06 + Math.random() * 0.04,
                0.1 + Math.random() * 0.08,
                0.08, 0.94,
                true, true, 0.9,
                1.5,
            );
        }

        // Dark smoke trailing (Sakurai: dark behind flame for contrast)
        if (Math.random() < 0.65) {
            const smokeAngle = Math.PI / 2 + (Math.random() - 0.5) * 0.4;
            core.spawnParticleAtAngle(
                x + (Math.random() - 0.5) * 14,
                y + 22 + Math.random() * 8,
                smokeAngle + (Math.random() - 0.5) * 0.3,
                1.0 + Math.random() * 1.5,
                T.ROCKET_SMOKE_COLOR,
                0.2 + Math.random() * 0.12,
                0.4 + Math.random() * 0.3,
                0.02, 0.95,
                false, false, 0.5,
            );
        }
    }
}

/**
 * Control Snap — "snap to attention" burst at post-launch ragdoll exit.
 * 7-layer Sakurai choreography (~25-30 particles). Signals "you can aim now."
 * Gold palette for both burst and grapple — gold = "momentum control system."
 */
export function spawnControlSnap(
    core: ParticleCore,
    cx: number, cy: number,
    playerColor: number,
): void {
    // === Layer 1: 8-10 radial speed lines (gold, hard, ADD) ===
    const lineCount = 8 + Math.floor(Math.random() * 3);
    for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
        const life = 0.08 + Math.random() * 0.06;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 8 + Math.random() * 4,
            outerRadius: 40 + Math.random() * 15,
            expandSpeed: 80 + Math.random() * 40,
            color: Math.random() < 0.25 ? 0xffffff : GOLD,
            lineWidth: 1.5 + Math.random() * 1.0,
            life,
            maxLife: life,
            freezeImmune: false,
        });
    }

    // === Layer 2: Core flash (gold, soft, ADD, 2.5x scale, life 0.10s) ===
    const flash = core.getInactive();
    if (flash) {
        configParticle(flash, core, {
            x: cx, y: cy,
            color: GOLD,
            life: 0.10,
            scale: 2.5,
            alpha: 0.65,
            rotationSpeed: 0,
            rotation: 0,
            hard: false,
        });
    }

    // === Layer 3: 6-8 snap sparks outward (white + gold, hard, ADD) ===
    const sparkCount = 6 + Math.floor(Math.random() * 3);
    for (let i = 0; i < sparkCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 4;
        const sparkColor = Math.random() < 0.35 ? 0xffffff : GOLD;
        core.spawnParticleAtAngle(
            cx + (Math.random() - 0.5) * 6,
            cy + (Math.random() - 0.5) * 6,
            angle, speed,
            sparkColor,
            0.12 + Math.random() * 0.08,
            0.25 + Math.random() * 0.15,
            0.02, 0.93,
            true, true, 0.85,
        );
    }

    // === Layer 4: 3-4 near-black soft particles at center (dark contrast cavity) ===
    const darkCount = 3 + Math.floor(Math.random() * 2);
    for (let i = 0; i < darkCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 8,
            y: cy + (Math.random() - 0.5) * 8,
            color: 0x1a0a00,
            life: 0.15 + Math.random() * 0.10,
            scale: 0.8 + Math.random() * 0.5,
            vx: Math.cos(angle) * (0.3 + Math.random() * 0.5),
            vy: Math.sin(angle) * (0.3 + Math.random() * 0.5),
            alpha: 0.5,
            friction: 0.9,
            rotationSpeed: 0,
            addBlend: false,
            hard: false,
        });
    }

    // === Layer 5: 2-3 player-color accent sparks (hard, ADD) ===
    const accentCount = 2 + Math.floor(Math.random() * 2);
    for (let i = 0; i < accentCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 3 + Math.random() * 2;
        core.spawnParticleAtAngle(
            cx + (Math.random() - 0.5) * 6,
            cy + (Math.random() - 0.5) * 6,
            angle, speed,
            playerColor,
            0.15 + Math.random() * 0.07,
            0.2 + Math.random() * 0.12,
            0.02, 0.92,
            true, true, 0.8,
        );
    }

    // === Layer 6: Shockwave ring (gold, 30→60px, expand 2.0, life 0.12s) ===
    core.spawnShockwave(cx, cy, GOLD, 60, 250, 2.0, 0.12);

    // === Layer 7: End pop (+80ms delayed) — white flash + dark pop ===
    core.pendingBlasts.push({
        timestamp: Date.now() + 80,
        callback: () => {
            const endFlash = core.getInactive();
            if (endFlash) {
                configParticle(endFlash, core, {
                    x: cx, y: cy,
                    color: 0xffffff,
                    life: 0.06,
                    scale: 1.5,
                    alpha: 0.55,
                    rotationSpeed: 0,
                    rotation: 0,
                    hard: false,
                });
            }
            const darkPop = core.getInactive();
            if (darkPop) {
                configParticle(darkPop, core, {
                    x: cx, y: cy,
                    color: BURST.CONTROL_FLASH_DARK,
                    life: 0.08,
                    scale: 1.2,
                    alpha: 0.4,
                    rotationSpeed: 0,
                    rotation: 0,
                    addBlend: false,
                    hard: false,
                });
            }
        },
    });
}

/**
 * Recovery Burst — defensive upward burst after DI knockback.
 * Scaled-down spawnBurstResolve (~60% intensity, gold palette).
 * 5-layer Sakurai choreography: speed lines → flash → streaks → shockwave → end flash.
 */
export function spawnRecoveryBurst(
    core: ParticleCore,
    cx: number, cy: number,
    launchVx: number, launchVy: number,
    playerColor: number,
): void {
    const launchAngle = Math.atan2(launchVy, launchVx);
    const launchSpeed = Math.sqrt(launchVx * launchVx + launchVy * launchVy);
    const intensity = Math.min(1.0, launchSpeed / 8) * 0.6; // 60% of full burst

    if (intensity < 0.05) return;

    // === Layer 1: Speed lines in launch direction ===
    const lineCount = 5 + Math.floor(4 * intensity);
    for (let i = 0; i < lineCount; i++) {
        const spread = (Math.random() - 0.5) * Math.PI * 0.5;
        const angle = launchAngle + spread;
        const life = 0.08 + Math.random() * 0.06;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 8 + Math.random() * 4,
            outerRadius: (25 + Math.random() * 20) * intensity,
            expandSpeed: (90 + Math.random() * 50) * intensity,
            color: Math.random() < 0.3 ? 0xffffff : GOLD,
            lineWidth: 2 + intensity,
            life,
            maxLife: life,
            freezeImmune: false,
        });
    }

    // === Layer 2: Launch flash ===
    const bigFlash = core.getInactive();
    if (bigFlash) {
        configParticle(bigFlash, core, {
            x: cx, y: cy,
            color: GOLD,
            life: 0.14,
            scale: 3.5 * intensity,
            vx: Math.cos(launchAngle) * 1,
            vy: Math.sin(launchAngle) * 1,
            alpha: 0.65,
            friction: 0.5,
            rotationSpeed: 0,
            rotation: 0,
            hard: false,
        });
    }

    // Dark contrast backing
    const darkFlash = core.getInactive();
    if (darkFlash) {
        configParticle(darkFlash, core, {
            x: cx, y: cy,
            color: 0x1a0a00,
            life: 0.16,
            scale: 2.2 * intensity,
            alpha: 0.5,
            rotationSpeed: 0,
            rotation: 0,
            addBlend: false,
            hard: false,
        });
    }

    // === Layer 3: Velocity-aligned gold streaks ===
    const streakCount = 6 + Math.floor(5 * intensity);
    for (let i = 0; i < streakCount; i++) {
        const spread = (Math.random() - 0.5) * Math.PI * 0.4;
        const angle = launchAngle + spread;
        const speed = (4 + Math.random() * 5) * intensity;
        const streakColor = Math.random() < 0.25 ? 0xffffff
            : Math.random() < 0.4 ? AMBER : GOLD;
        const dist = 10 + Math.random() * 8;
        const offX = cx + Math.cos(launchAngle) * dist;
        const offY = cy + Math.sin(launchAngle) * dist;
        core.spawnParticleAtAngle(
            offX, offY,
            angle, speed,
            streakColor,
            0.1 + Math.random() * 0.1,
            (0.25 + Math.random() * 0.25) * intensity,
            0.03, 0.95,
            true, true, 0.85,
            2.0 + Math.random() * 1.5,
        );
    }

    // === Layer 4: Directional shockwave ===
    core.spawnShockwave(
        cx + Math.cos(launchAngle) * 8,
        cy + Math.sin(launchAngle) * 8,
        GOLD,
        35 * intensity,
        120,
        2.0,
        0.15,
    );

    // === Layer 5: End flash (+100ms) ===
    core.pendingBlasts.push({
        timestamp: Date.now() + 100,
        callback: () => {
            const endFlash = core.getInactive();
            if (endFlash) {
                configParticle(endFlash, core, {
                    x: cx + Math.cos(launchAngle) * 10,
                    y: cy + Math.sin(launchAngle) * 10,
                    color: 0xffffff,
                    life: 0.06,
                    scale: 2.5 * intensity,
                    alpha: 0.55,
                    rotationSpeed: 0,
                    rotation: 0,
                    hard: false,
                });
            }
        },
    });
}

// Green palette constants for brace
const GREEN = BRACE.GREEN;
const GREEN_BRIGHT = BRACE.GREEN_BRIGHT;
const GREEN_DARK = BRACE.GREEN_DARK;

/**
 * Brace Heal — grounded heal burst during burst window.
 * 4-layer Sakurai choreography, green palette, ~20-25 particles.
 * Lighter than burst resolve (8 layers) — it's a defensive choice, not an explosive one.
 *
 * Layer 1: Core green flash + dark contrast backing
 * Layer 2: 12-16 rising sparkles (negative gravity — float upward like Smash tomato)
 * Layer 3: 4-6 dark green puffs (NORMAL blend depth)
 * Layer 4a: Green shockwave ring
 * Layer 4b: +80ms delayed sparkles + end flash
 */
export function spawnBraceHeal(
    core: ParticleCore,
    cx: number, cy: number,
    playerColor: number,
): void {
    // === Layer 1: Core green flash (soft, ADD, 4.0 scale) + dark backing ===
    const flash = core.getInactive();
    if (flash) {
        configParticle(flash, core, {
            x: cx, y: cy,
            color: GREEN,
            life: 0.18,
            scale: 4.0,
            alpha: 0.8,
            rotationSpeed: 0,
            rotation: 0,
            hard: false,
        });
    }

    // Dark green backing (Sakurai: dark behind bright for contrast)
    const darkBacking = core.getInactive();
    if (darkBacking) {
        configParticle(darkBacking, core, {
            x: cx, y: cy,
            color: GREEN_DARK,
            life: 0.2,
            scale: 3.0,
            alpha: 0.6,
            rotationSpeed: 0,
            rotation: 0,
            addBlend: false,
            hard: false,
        });
    }

    // === Layer 2: 28-34 rising sparkles (upward fan ±60°, negative gravity) ===
    // Smash tomato glistening — dense, crisp, overwhelmingly green
    // Sakurai: smaller particles read as more numerous; mix NORMAL into ADD for crispness
    const sparkCount = 28 + Math.floor(Math.random() * 7);
    for (let i = 0; i < sparkCount; i++) {
        // Wider upward fan: -90° ± 60°
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.67;
        const speed = 2.5 + Math.random() * 5;
        // Color mix: 55% green, 25% bright green, 12% white-green, 8% pure white — heavily green
        const roll = Math.random();
        const sparkColor = roll < 0.08 ? 0xffffff
            : roll < 0.20 ? 0xaaffcc
                : roll < 0.45 ? GREEN_BRIGHT
                    : GREEN;
        // White and white-green sparkles use NORMAL blend for crispness among the glow
        const useAdd = sparkColor !== 0xffffff && sparkColor !== 0xaaffcc;
        // Spread across 40px width (no center pileup)
        const offsetX = (Math.random() - 0.5) * 40;
        const offsetY = (Math.random() - 0.5) * 12;
        core.spawnParticleAtAngle(
            cx + offsetX, cy + offsetY,
            angle, speed,
            sparkColor,
            0.3 + Math.random() * 0.2,   // capped at 0.5s — brief and punchy
            0.15 + Math.random() * 0.2,   // smaller = denser perceived count
            -0.03, 0.97,                  // stronger negative gravity = float up harder
            true, useAdd, useAdd ? 0.9 : 1.0,
        );
    }

    // === Layer 2b: 6-8 radial speed lines — crisp geometric burst ===
    const lineCount = 6 + Math.floor(Math.random() * 3);
    for (let i = 0; i < lineCount; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
        const life = 0.1 + Math.random() * 0.06;
        core.impactLines.push({
            x: cx, y: cy,
            angle,
            innerRadius: 8 + Math.random() * 4,
            outerRadius: 30 + Math.random() * 20,
            expandSpeed: 80 + Math.random() * 40,
            color: Math.random() < 0.2 ? 0xffffff : GREEN,
            lineWidth: 2.0 + Math.random() * 1.0,
            life,
            maxLife: life,
            freezeImmune: false,
        });
    }

    // === Layer 3: 6-8 dark green puffs (soft, NORMAL blend) ===
    const puffCount = 6 + Math.floor(Math.random() * 3);
    for (let i = 0; i < puffCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
            x: cx + (Math.random() - 0.5) * 20,
            y: cy + (Math.random() - 0.5) * 12,
            color: GREEN_DARK,
            life: 0.25 + Math.random() * 0.15,
            scale: 0.8 + Math.random() * 0.5,
            vx: Math.cos(angle) * (0.4 + Math.random() * 0.6),
            vy: Math.sin(angle) * (0.4 + Math.random() * 0.6) - 0.5, // upward drift
            alpha: 0.5,
            friction: 0.92,
            rotationSpeed: 0,
            addBlend: false,
            hard: false,
        });
    }

    // === Layer 4a: Green shockwave ring (bigger, punchier) ===
    core.spawnShockwave(cx, cy, GREEN, 55, 130, 2.5, 0.18);

    // Second ring — white for contrast
    core.spawnShockwave(cx, cy, 0xffffff, 35, 100, 1.5, 0.12);

    // === Layer 4b: +80ms delayed sparkles (dense second wave) + end flash ===
    core.pendingBlasts.push({
        timestamp: Date.now() + 80,
        callback: () => {
            // 8-12 delayed sparkles (dense second wave)
            const delayedCount = 8 + Math.floor(Math.random() * 5);
            for (let i = 0; i < delayedCount; i++) {
                const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6;
                const speed = 2 + Math.random() * 3.5;
                const sparkColor = Math.random() < 0.15 ? 0xffffff
                    : Math.random() < 0.4 ? GREEN_BRIGHT : GREEN;
                const useAdd = sparkColor !== 0xffffff;
                core.spawnParticleAtAngle(
                    cx + (Math.random() - 0.5) * 30,
                    cy + (Math.random() - 0.5) * 8,
                    angle, speed,
                    sparkColor,
                    0.25 + Math.random() * 0.15,  // capped shorter
                    0.15 + Math.random() * 0.15,   // smaller for density
                    -0.03, 0.96,
                    true, useAdd, useAdd ? 0.85 : 1.0,
                );
            }

            // End flash (bright green, bigger)
            const endFlash = core.getInactive();
            if (endFlash) {
                configParticle(endFlash, core, {
                    x: cx, y: cy,
                    color: GREEN_BRIGHT,
                    life: 0.09,
                    scale: 3.0,
                    alpha: 0.6,
                    rotationSpeed: 0,
                    rotation: 0,
                    hard: false,
                });
            }

            // Dark contrast pop
            const darkPop = core.getInactive();
            if (darkPop) {
                configParticle(darkPop, core, {
                    x: cx, y: cy,
                    color: GREEN_DARK,
                    life: 0.1,
                    scale: 2.0,
                    alpha: 0.45,
                    rotationSpeed: 0,
                    rotation: 0,
                    addBlend: false,
                    hard: false,
                });
            }
        },
    });
}
